<template>
  <div
    class="flex h-full min-h-0 min-w-0 flex-col overflow-hidden lg:rounded-lg"
  >
    <ConnectionStatus
      v-if="!isConnected || isReconnecting"
      :is-connected="isConnected"
      :is-reconnecting="isReconnecting"
      :reconnect-attempts="reconnectAttempts"
      :reconnect-delay="currentReconnectDelay"
      :max-reconnect-attempts="10"
    />

    <div class="flex min-h-0 min-w-0 flex-1 overflow-visible lg:gap-2 lg:pl-1 lg:pb-1 lg:pr-0.5">
      <div
        :class="[
          'h-full w-full shrink-0 overflow-hidden shadow-sm lg:rounded-lg',
          'md:block md:w-[260px]',
          'lg:w-[300px]',
          '2xl:w-[420px] ',
          showMobileChatList ? 'block' : 'hidden md:block',
        ]"
      >
        <ChatList
          :chats="sortedChats"
          :selected-chat-id="selectedChatId"
          :current-user-id="currentUserId"
          :requested-tab="requestedChatListTab"
          @select-chat="handleSelectChat"
          @open-template-dialog="showTemplateDialog = true"
          class="mb-1"
        />
      </div>

      <div
        :class="[
          'relative flex-1 h-full overflow-hidden shadow-sm lg:rounded-lg gap-1',
          'md:flex',
          showMobileChatList ? 'hidden md:flex' : 'flex',
        ]"
      >
        <div v-if="selectedChat" class="min-h-0 min-w-0 flex-1">
          <ChatView
            :contact-name="selectedChat.contact_name"
            :contact-profile-name="selectedContactInfo?.name"
            :contact-username="selectedChat.contact_username"
            :contact-phone="selectedChat.contact_phone"
            :messages="selectedChat.messages"
            :is-taking-chat="isTakingSelectedChat"
            :is-loading-history="isSelectedChatHistoryLoading"
            :has-more-history="selectedChatHasMoreHistory"
            :chat="{
              id: selectedChat.chat_id,
              user_id: selectedChat.user_id ?? null,
              status: selectedChat.status,
              campaign_id: selectedChat.campaign_id,
              campaign_name:
                selectedChat.campaign_name || campaignMap.get(selectedChat.campaign_id)?.name || '',
              incoming_route_id: selectedChat.incoming_route_id,
              channel_id: selectedChat.channel_id,
              start_date: selectedChat.created_at,
              last_message_date: selectedChat.last_message_time,
            }"
            :current-user-id="currentUserId ?? ''"
            @send-message="handleSendMessage"
            @send-file="handleSendFile"
            @send-template="handleSendTemplate"
            @end-chat="handleEndChat"
            @close-chat="handleCloseChat"
            @transfer-chat="handleOpenTransferDialog"
            @take-chat="handleTakeChat"
            @back-to-list="showMobileChatList = true"
            @load-history="handleLoadSelectedChatHistory"
            @open-contact-info="handleOpenContactInfo"
          />
        </div>

        <Transition name="contact-info-panel">
          <ContactInfoPanel
            v-if="selectedChat && showContactInfoPanel"
            class="absolute inset-y-0 right-0 z-20 w-full max-w-[360px] shadow-lg 2xl:static 2xl:z-auto 2xl:w-[380px] 2xl:max-w-none 2xl:shadow-sm"
            :contact-data="selectedContactInfo"
            :contact-name="selectedChat.contact_name"
            :contact-phone="selectedChat.contact_phone"
            :contact-username="selectedChat.contact_username"
            :channel-id="selectedChat.channel_id"
            :origin="selectedChat.origin"
            :campaign-name="
              selectedChat.campaign_name || campaignMap.get(selectedChat.campaign_id)?.name || ''
            "
            :mask-number="selectedCampaignMaskNumber"
            :loading="isContactInfoLoading"
            :error="contactInfoError"
            @close="showContactInfoPanel = false"
            @retry="loadSelectedContactInfo"
          />
        </Transition>

        <div
          v-if="!selectedChat"
          class="flex items-center justify-center w-full app-card-glass shadow-xl rounded-lg"
        >
          <div
            class="text-center max-w-sm px-6 py-8 rounded-2xl bg-white/40 dark:bg-[var(--p-primary-900)]/20 backdrop-blur-md border border-white/20 dark:border-white/5 shadow-sm"
          >
            <div
              class="w-16 h-16 bg-gradient-to-br from-[var(--p-primary-50)] to-[var(--p-primary-100)] dark:from-[var(--p-primary-950)]/40 dark:to-[var(--p-primary-900)]/20 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-xs border border-[var(--p-primary-100)]/50 dark:border-[var(--p-primary-800)]/30 animate-pulse"
            >
              <svg
                class="w-8 h-8 text-[var(--p-primary-color)]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
            </div>
            <h2
              class="text-sm font-semibold text-[var(--p-primary-color)] dark:text-[var(--p-surface-0)] mb-1.5"
            >
              {{ $t('whatsapp.main.web_client') }}
            </h2>
            <p
              class="text-xs text-[var(--p-surface-600)] dark:text-[var(--p-surface-0)]/80 leading-relaxed"
            >
              {{
                $t(
                  sortedChats.length
                    ? 'whatsapp.main.select_active_conversation'
                    : 'whatsapp.main.no_chats_to_show',
                )
              }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <SendTemplateDialog
      v-model:visible="showTemplateDialog"
      :initial-campaign-id="initialTemplateCampaignId"
      :start-conversation="true"
      @send="handleSendTemplate"
    />

    <TransferChatDialog
      v-if="selectedChat"
      v-model:visible="showTransferDialog"
      :chat-id="selectedChat.chat_id"
      :contact-name="selectedChat.contact_name"
      :contact-phone="
        getContactIdentifier(selectedChat.contact_phone, selectedChat.contact_username)
      "
      :current-campaign-id="selectedChat.campaign_id"
      :current-campaign-name="selectedChat.campaign_name || ''"
      :channel-id="selectedChat.channel_id"
      :current-user-id="currentUserId || ''"
      :current-user-name="$t('whatsapp.main.current_user')"
      :is-awaiting-transfer-response="isAwaitingTransferToUserResponse"
      :restore-draft="transferDialogRestoreDraft"
      @transfer="handleTransferChat"
    />

    <TransferRequestDialog
      v-if="currentTransferRequest"
      v-model:visible="showTransferRequestDialog"
      :chat-id="currentTransferRequest.chatId"
      :contact-name="currentTransferRequest.contactName"
      :contact-phone="currentTransferRequest.contactPhone"
      :transfer-message="currentTransferRequest.transfer_message"
      :from-user-id="currentTransferRequest.fromUserId"
      :from-user-name="currentTransferRequest.fromUserName"
      :from-campaign-id="currentTransferRequest.fromCampaignId"
      :from-campaign-name="currentTransferRequest.fromCampaignName"
      :to-user-id="currentTransferRequest.toUserId"
      :to-user-name="currentTransferRequest.toUserName"
      :to-campaign-id="currentTransferRequest.toCampaignId"
      :to-campaign-name="currentTransferRequest.toCampaignName"
      :countdown-seconds="transferCountdownSeconds"
      :is-processing="isProcessingTransferRequest"
      @accept="handleAcceptTransfer"
      @reject="handleRejectTransfer"
    />

    <TransferRejectedDialog
      v-if="rejectedTransfer"
      v-model:visible="showTransferRejectedDialog"
      :chat-id="rejectedTransfer.chatId"
      :contact-name="rejectedTransfer.contactName"
      :contact-phone="rejectedTransfer.contactPhone"
      :destination-name="rejectedTransfer.destinationName"
      :destination-subtitle="rejectedTransfer.destinationSubtitle"
      :destination-meta="rejectedTransfer.destinationMeta"
      :can-retry-transfer="Boolean(rejectedTransfer.retryTransferData)"
      @back-to-chat="handleBackToChatFromRejected"
      @change-destination="handleChangeDestinationFromRejected"
      @retry="handleRetryRejectedTransfer"
    />

    <TransferAcceptedDialog
      v-if="acceptedTransfer"
      v-model:visible="showTransferAcceptedDialog"
      :chat-id="acceptedTransfer.chatId"
      :contact-name="acceptedTransfer.contactName"
      :contact-phone="acceptedTransfer.contactPhone"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, nextTick, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { isAxiosError } from 'axios'
import { useToast } from 'primevue/usetoast'
import ChatList from '@/components/whatsapp/organisms/lists/ChatList.vue'
import ChatView from '@/components/whatsapp/organisms/chat-view/ChatView.vue'
import ContactInfoPanel from '@/components/whatsapp/organisms/panels/ContactInfoPanel.vue'
import ConnectionStatus from '@/components/whatsapp/atoms/indicators/ConnectionStatus.vue'
import SendTemplateDialog from '@/components/whatsapp/organisms/dialogs/SendTemplateDialog.vue'
import TransferChatDialog from '@/components/whatsapp/organisms/dialogs/TransferChatDialog.vue'
import TransferRequestDialog from '@/components/whatsapp/organisms/dialogs/TransferRequestDialog.vue'
import TransferRejectedDialog from '@/components/whatsapp/molecules/dialogs/TransferRejectedDialog.vue'
import TransferAcceptedDialog from '@/components/whatsapp/molecules/dialogs/TransferAcceptedDialog.vue'
import { useWhatsAppCoordinator } from '@/components/whatsapp/stores/useWhatsAppCoordinator'
import { useChatDraftStore } from '@/components/whatsapp/stores/useChatDraftStore'
import { MessageNotQueuedError } from '@/components/whatsapp/services/chatOperations'
import { useAuthStore } from '@/stores/useAuthStore'
import { useCampaignService } from '@/services/campaign/useCampaignService'
import { useChatService } from '@/services/chat/useChatService'
import { useContactService } from '@/services/contact/useContactService'
import type {
  Chat as ApiChat,
  ChatListResponse,
  Message as ApiMessage,
} from '@/services/chat/useChatService'
import { useTransferRequests } from '@/composables/useTransferRequests'
import { useNotifications } from '@/composables/useNotifications'
import type {
  Template,
  ContactSelection,
  RoutingDestination,
  Message,
  Chat as WhatsAppChat,
  UserTookChatEvent,
  TransferAcceptedEvent,
  TemplateSendComponent,
} from '@/components/whatsapp/types/interfaces'
import type { IOptionsCampaign } from '@/services/campaign/interfaces/options-campaign.interface'
import type { ContactDto } from '@/services/contact/interfaces/contact.interface'
import {
  UserRole,
  ChatStatus,
  MessageSourceType,
  MessageStatus,
  MessageType,
} from '@/components/whatsapp/types/enums'
import {
  getMessagePreviewText,
  normalizeMessageStatusValue,
} from '@/components/whatsapp/services/messageContentFormatter'
import { getMessageFailure } from '@/components/whatsapp/services/messageFailureReason'
import { sortChatMessages } from '@/components/whatsapp/services/messageOrdering'
import {
  getContactIdentifier,
  resolveContactName,
  toChatIdentity,
} from '@/components/whatsapp/services/contactIdentity'
import { WHATSAPP_CHANNEL_ID } from '@/components/whatsapp/services/chatChannel'
import { getLastVisibleMessage } from '@/components/whatsapp/services/messageVisibility'

const whatsAppCoordinator = useWhatsAppCoordinator()
const authStore = useAuthStore()
const campaignService = useCampaignService()
const chatService = useChatService()
const contactService = useContactService()
const transferRequests = useTransferRequests()
const notifications = useNotifications()
const route = useRoute()
const router = useRouter()
const chatDraftStore = useChatDraftStore()
const { t } = useI18n()
const toast = useToast()
const { currentTransferRequest, pendingTransfers } = transferRequests

// Referencias reactivas desde los stores especializados
const { sortedChats, selectedChatId, selectedChat, currentUserId } = storeToRefs(
  whatsAppCoordinator.chatStore,
)

const { isConnected, isReconnecting, reconnectAttempts, currentReconnectDelay } = storeToRefs(
  whatsAppCoordinator.webSocketStore,
)

const showTemplateDialog = ref(false)
const initialTemplateCampaignId = ref<string | undefined>()
const showTransferDialog = ref(false)
const showContactInfoPanel = ref(false)
const selectedContactInfo = ref<ContactDto | null>(null)
const isContactInfoLoading = ref(false)
const contactInfoError = ref<string | null>(null)
const contactInfoCache = ref<Map<string, ContactDto | null>>(new Map())
const showTransferRequestDialog = ref(false)
const showTransferRejectedDialog = ref(false)
const showTransferAcceptedDialog = ref(false)
const showMobileChatList = ref(true)
const campaignMap = ref<Map<string, IOptionsCampaign>>(new Map())
const pendingTakeChatIds = ref(new Set<string>())
const loadingHistoryChatIds = reactive(new Set<string>())
const exhaustedHistoryChatIds = reactive(new Set<string>())
const transferCountdownSeconds = ref(20)
const transferCountdownInterval = ref<number | null>(null)
const isProcessingTransferRequest = ref(false)
const pendingTransferResponseChatId = ref<string | null>(null)
const isAwaitingTransferToUserResponse = ref(false)
const pendingTransferToUserChatId = ref<string | null>(null)
const pendingTransferToUserInfo = ref<RejectedTransferInfo | null>(null)
const transferDialogRestoreDraft = ref<{
  nonce: number
  action: 'type' | 'retry'
  transferData?: Extract<TransferChatData, { transferType: 'user' }> | null
} | null>(null)
// Cursor del historial por chat: el id del mensaje mas viejo ya traido, que es desde
// donde arranca la siguiente pagina.
const historyCursorByChatId = reactive(new Map<string, string>())

interface RejectedTransferInfo {
  chatId: string
  contactName: string
  contactPhone: string
  destinationName?: string
  destinationSubtitle?: string | null
  destinationMeta?: string | null
  retryTransferData?: Extract<TransferChatData, { transferType: 'user' }> | null
}

const rejectedTransfer = ref<RejectedTransferInfo | null>(null)
const acceptedTransfer = ref<RejectedTransferInfo | null>(null)

// La vista de WhatsApp nunca se desmonta (v-show en el layout), asi que los
// chips de la barra de campanas piden la pestana por query y se lee aqui. Se
// suelta despues de aplicarla —vuelve a null y se limpia la query— para que un
// segundo clic en el mismo chip la vuelva a pedir.
const requestedChatListTab = ref<string | null>(null)

const applyChatListTabFromRoute = async () => {
  const rawTab = route.query.tab
  const tab = Array.isArray(rawTab) ? rawTab[0] : rawTab
  if (!tab) return

  requestedChatListTab.value = String(tab)

  const query = { ...route.query }
  delete query.tab
  void router.replace({ query })

  await nextTick()
  requestedChatListTab.value = null
}

const openStartConversationFromRoute = () => {
  if (route.query.open !== 'start-conversation') return

  const campaignId = route.query.campaignId
  const normalizedCampaignId = Array.isArray(campaignId) ? campaignId[0] : campaignId
  initialTemplateCampaignId.value = normalizedCampaignId ? String(normalizedCampaignId) : undefined
  showTemplateDialog.value = true
}

const isTakingSelectedChat = computed(() => {
  if (!selectedChat.value) return false
  return pendingTakeChatIds.value.has(String(selectedChat.value.chat_id))
})

const isSelectedChatHistoryLoading = computed(() => {
  if (!selectedChat.value) return false
  return loadingHistoryChatIds.has(selectedChat.value.chat_id)
})

const selectedChatHasMoreHistory = computed(() => {
  if (!selectedChat.value) return false
  return !exhaustedHistoryChatIds.has(selectedChat.value.chat_id)
})

const selectedCampaignMaskNumber = computed(() => {
  if (!selectedChat.value) return false

  const campaignIds = [
    selectedChat.value.campaign_id,
    selectedContactInfo.value?.campaign_id,
  ].filter(Boolean)
  const campaigns = Array.from(campaignMap.value.values())
  const campaign =
    campaignIds.map((campaignId) => campaignMap.value.get(campaignId!)).find(Boolean) ||
    campaigns.find((item) => campaignIds.some((campaignId) => String(item.id) === campaignId)) ||
    campaigns.find((item) => item.name === selectedChat.value?.campaign_name) ||
    campaignIds
      .map((campaignId) => whatsAppCoordinator.chatStore.getCampaign(campaignId!))
      .find(Boolean)
  const maskValue: unknown = campaign?.masked_number

  if (typeof maskValue === 'string') {
    return maskValue.trim().toLowerCase() === 'true' || maskValue.trim() === '1'
  }

  return maskValue === true || maskValue === 1
})

const getChatContactInfoKey = (
  chat: Pick<WhatsAppChat, 'chat_id' | 'contact_phone' | 'campaign_id'>,
) => {
  // Sin teléfono la llave es el chat: con el teléfono vacío todos los clientes
  // sin teléfono de la campaña compartirían la misma entrada.
  return `${chat.contact_phone || chat.chat_id}:${chat.campaign_id}`
}

const getContactInfoKey = () => {
  if (!selectedChat.value) return ''
  return getChatContactInfoKey(selectedChat.value)
}

const loadSelectedContactInfo = async () => {
  if (!selectedChat.value) return

  const contactInfoKey = getContactInfoKey()

  contactInfoError.value = null

  // El cache guarda tambien la ausencia (null). Se consulta con has() y no con
  // el valor, para distinguir "todavia no se pidio" de "se pidio y no existe".
  if (contactInfoCache.value.has(contactInfoKey)) {
    const cachedContactInfo = contactInfoCache.value.get(contactInfoKey) ?? null
    selectedContactInfo.value = cachedContactInfo

    if (cachedContactInfo) {
      whatsAppCoordinator.chatStore.updateChat(selectedChat.value.chat_id, {
        contact_profile_name: cachedContactInfo.name,
      })
    }
    return
  }

  selectedContactInfo.value = null

  // Los contactos de campaña se buscan por teléfono: el cliente que solo se
  // identifica por BSUID no puede estar en la base, y buscar con el teléfono
  // vacío traería cualquier contacto.
  if (!selectedChat.value.contact_phone) {
    contactInfoCache.value.set(contactInfoKey, null)
    return
  }

  isContactInfoLoading.value = true

  try {
    const contactInfo = await contactService.searchContact(
      {
        phone: selectedChat.value.contact_phone,
        campaign_id: selectedChat.value.campaign_id,
      },
      { showErrorToast: false },
    )

    contactInfoCache.value.set(contactInfoKey, contactInfo)

    if (contactInfoKey === getContactInfoKey()) {
      selectedContactInfo.value = contactInfo
      whatsAppCoordinator.chatStore.updateChat(selectedChat.value.chat_id, {
        contact_profile_name: contactInfo.name,
      })
    }
  } catch (error) {
    // Que el telefono no este en la base de contactos de la campana es un caso
    // normal (el cliente escribe sin estar cargado), no un fallo: se cachea la
    // ausencia y el panel se queda con el nombre y telefono que ya trae el
    // chat, sin mensaje de error ni boton de reintentar.
    const contactNotFound = isAxiosError(error) && error.response?.status === 404

    if (contactNotFound) {
      contactInfoCache.value.set(contactInfoKey, null)
    }

    if (contactInfoKey === getContactInfoKey()) {
      selectedContactInfo.value = null
      contactInfoError.value = contactNotFound ? null : t('whatsapp.main.contact_info_load_error')
    }
  } finally {
    if (contactInfoKey === getContactInfoKey()) {
      isContactInfoLoading.value = false
    }
  }
}

const handleOpenContactInfo = () => {
  showContactInfoPanel.value = true
  void loadSelectedContactInfo()
}

const isMediaSource = (value: unknown): value is string => {
  if (typeof value !== 'string') return false

  return /^(https?:|blob:|data:|\/)/i.test(value)
}

const normalizeMediaCaption = (caption: unknown, messageType: MessageType) => {
  if (typeof caption !== 'string') return null

  const trimmedCaption = caption.trim()
  const isVisualMedia = messageType === MessageType.IMAGE || messageType === MessageType.VIDEO
  if (!trimmedCaption || (isVisualMedia && trimmedCaption.toLowerCase() === messageType)) {
    return null
  }

  return caption
}

const normalizeApiMessage = (msg: ApiMessage) => {
  let messageContent
  try {
    const parsedContent = typeof msg.content === 'string' ? JSON.parse(msg.content) : msg.content
    const contentObject =
      parsedContent && typeof parsedContent === 'object'
        ? (parsedContent as Record<string, any>)
        : {}

    const messageType = (String(contentObject.type || 'text').toLowerCase() ||
      'text') as MessageType
    const caption = normalizeMediaCaption(contentObject.caption, messageType)
    const rawContext =
      contentObject.context && typeof contentObject.context === 'object'
        ? (contentObject.context as Record<string, any>)
        : null

    const context = rawContext
      ? {
          from: rawContext.from || null,
          from_user_id: rawContext.from_user_id || null,
          id: rawContext.id || null,
          gs_id: rawContext.gs_id || null,
          meta_msg_id: rawContext.meta_msg_id || null,
        }
      : {
          from: null,
          from_user_id: null,
          id: null,
          gs_id: null,
          meta_msg_id: null,
        }

    messageContent = {
      type: messageType,
      content: messageType === MessageType.AUDIO ? caption || '' : contentObject.content || '',
      url:
        messageType === MessageType.AUDIO
          ? contentObject.url ||
            (isMediaSource(contentObject.content) ? contentObject.content : null)
          : contentObject.url || null,
      file_name: contentObject.file_name || null,
      file_size: contentObject.file_size || null,
      content_type: contentObject.content_type || contentObject.file_content_type || null,
      duration: contentObject.duration || null,
      longitude: contentObject.longitude || null,
      latitude: contentObject.latitude || null,
      name: contentObject.name || null,
      address: contentObject.address || null,
      contacts: Array.isArray(contentObject.contacts) ? contentObject.contacts : null,
      context,
      caption,
      ai_translate:
        contentObject.ai_translate && typeof contentObject.ai_translate === 'object'
          ? contentObject.ai_translate
          : null,
    }
  } catch {
    messageContent = {
      type: MessageType.TEXT,
      content: typeof msg.content === 'string' ? msg.content : '',
      url: null,
      file_name: null,
      longitude: null,
      latitude: null,
      context: {
        from: null,
        from_user_id: null,
        id: null,
      },
      caption: null,
    }
  }

  const status = normalizeMessageStatusValue(msg.status) || MessageStatus.SEND
  // El motivo del fallo sale del último webhook de estado que guardó el mensaje.
  const failure =
    status === MessageStatus.FAILED ? getMessageFailure(msg.provider_response) : null

  return {
    id: msg.id,
    chat_id: msg.chat_id,
    source_type:
      msg.source_type === 'ENTRANTE'
        ? MessageSourceType.ENTRANTE
        : msg.source_type === 'TEMPLATE'
          ? MessageSourceType.TEMPLATE
          : MessageSourceType.SALIENTE,
    date: msg.date.toString(),
    provider_message_id: msg.provider_message_id,
    consecutive: msg.consecutive,
    status,
    content: messageContent,
    user_id: msg.user_id || null,
    failure_code: failure?.code || null,
    failure_reason: failure?.reason || null,
  }
}

const addPendingTakeChat = (chatId: string) => {
  pendingTakeChatIds.value = new Set(pendingTakeChatIds.value).add(chatId)
}

const clearPendingTakeChat = (chatId: string) => {
  const nextPendingTakeChatIds = new Set(pendingTakeChatIds.value)
  nextPendingTakeChatIds.delete(chatId)
  pendingTakeChatIds.value = nextPendingTakeChatIds
}

const handleUserTookChatConfirmation = (data: unknown) => {
  const tookChatEvent = data as UserTookChatEvent
  if (!tookChatEvent?.chat_id) return
  clearPendingTakeChat(String(tookChatEvent.chat_id))
}

const clearTransferCountdown = () => {
  if (transferCountdownInterval.value !== null) {
    clearInterval(transferCountdownInterval.value)
    transferCountdownInterval.value = null
  }
}

const startTransferCountdown = () => {
  clearTransferCountdown()
  transferCountdownSeconds.value = 20

  transferCountdownInterval.value = window.setInterval(() => {
    if (isProcessingTransferRequest.value) {
      return
    }

    if (transferCountdownSeconds.value <= 1) {
      clearTransferCountdown()

      if (currentTransferRequest.value) {
        void submitTransferDecision(currentTransferRequest.value.chatId, false)
      }
      return
    }

    transferCountdownSeconds.value -= 1
  }, 1000)
}

const findTransferRequestByChatId = (chatId: string) => {
  return pendingTransfers.value.find((transfer) => transfer.chatId === chatId) || null
}

const buildTransferredChatModel = (
  transferResponse: TransferAcceptedEvent,
  options?: {
    fallbackContactName?: string
    fallbackContactPhone?: string
    fallbackCampaignId?: string
    fallbackCampaignName?: string
  },
) => {
  const eventChat = transferResponse.chat
  if (!eventChat) return null

  const normalizedMessages = (eventChat.messages || []).map((message) => ({
    ...message,
    source_type:
      message.source_type === MessageSourceType.ENTRANTE
        ? MessageSourceType.ENTRANTE
        : message.source_type === MessageSourceType.TEMPLATE
          ? MessageSourceType.TEMPLATE
          : MessageSourceType.SALIENTE,
    content: message.content,
  }))

  normalizedMessages.sort((a, b) => a.consecutive - b.consecutive)

  const lastMessage = normalizedMessages[normalizedMessages.length - 1]
  const campaignId = eventChat.campaign_id || options?.fallbackCampaignId || ''
  const campaignName =
    options?.fallbackCampaignName || campaignMap.value.get(campaignId)?.name || ''

  return {
    id: eventChat.id || eventChat.chat_id || transferResponse.chat_id,
    chat_id: eventChat.id || eventChat.chat_id || transferResponse.chat_id,
    contact_name:
      eventChat.contact_name ||
      resolveContactName(eventChat.name, eventChat.username, eventChat.phone) ||
      options?.fallbackContactName ||
      t('whatsapp.main.unknown_contact'),
    contact_phone:
      eventChat.contact_phone || eventChat.phone || options?.fallbackContactPhone || '',
    contact_username: eventChat.username || null,
    channel_id: eventChat.channel_id || WHATSAPP_CHANNEL_ID,
    origin: eventChat.origin ?? null,
    last_message: getMessagePreviewText(lastMessage?.content),
    last_message_time: lastMessage?.date || eventChat.start_date || new Date().toISOString(),
    unread_count: 0,
    status: eventChat.status || ChatStatus.ATTENTION,
    campaign_id: campaignId,
    campaign_name: campaignName,
    incoming_route_id: eventChat.incoming_route_id || '',
    messages: normalizedMessages,
    user_id: currentUserId.value || null,
    created_at: eventChat.start_date,
    assigned_at: new Date().toISOString(),
  }
}

const getSessionIdentifier = () =>
  localStorage.getItem('oper_session_identifier') ||
  authStore.currentUser?.email ||
  authStore.userAlias ||
  ''

const isRegisteredInCampaign = async () => {
  const sessionIdentifier = getSessionIdentifier()
  if (!sessionIdentifier) return false

  try {
    const sessionStatus = await authStore.checkSessionStatus(sessionIdentifier)
    return (
      sessionStatus.has_session && sessionStatus.campaigns.some((campaign) => campaign.is_logged_in)
    )
  } catch {
    return false
  }
}

const syncTransferRequestDialogState = () => {
  if (currentTransferRequest.value) {
    showTransferRequestDialog.value = true
    startTransferCountdown()
    return
  }

  showTransferRequestDialog.value = false
}

const submitTransferDecision = async (chatId: string, isAccepted: boolean) => {
  if (isProcessingTransferRequest.value) return

  const transferRequest = findTransferRequestByChatId(chatId)
  if (!transferRequest) return

  isProcessingTransferRequest.value = true
  pendingTransferResponseChatId.value = chatId
  clearTransferCountdown()

  if (!isAccepted) {
    showTransferRequestDialog.value = false
  }

  try {
    await whatsAppCoordinator.confirmTransfer(
      chatId,
      isAccepted,
      transferRequest.fromUserId,
      transferRequest.fromUserName,
      transferRequest.toUserId,
      transferRequest.toUserName,
      transferRequest.toCampaignId,
    )

    // El rechazo puede no disparar un evento de confirmacion desde backend.
    // Limpiamos el estado local para no dejar bloqueada la cola de solicitudes.
    if (!isAccepted) {
      isProcessingTransferRequest.value = false
      pendingTransferResponseChatId.value = null
      transferRequests.rejectTransfer(chatId)
      syncTransferRequestDialogState()
    }
  } catch (error) {
    isProcessingTransferRequest.value = false
    pendingTransferResponseChatId.value = null
    syncTransferRequestDialogState()
  }
}

const handleTransferAcceptedResponse = (data: unknown) => {
  const transferResponse = data as TransferAcceptedEvent
  if (!transferResponse?.chat_id) return

  if (pendingTransferToUserChatId.value === transferResponse.chat_id) {
    isAwaitingTransferToUserResponse.value = false
    pendingTransferToUserChatId.value = null

    if (transferResponse.is_transfered) {
      showTransferDialog.value = false
      if (pendingTransferToUserInfo.value) {
        acceptedTransfer.value = { ...pendingTransferToUserInfo.value }
        showTransferAcceptedDialog.value = true
      }
    } else {
      showTransferDialog.value = false
      if (pendingTransferToUserInfo.value) {
        rejectedTransfer.value = { ...pendingTransferToUserInfo.value }
        showTransferRejectedDialog.value = true
      }
    }

    pendingTransferToUserInfo.value = null
    return
  }

  if (pendingTransferResponseChatId.value !== transferResponse.chat_id) return

  const transferRequest = findTransferRequestByChatId(transferResponse.chat_id)

  isProcessingTransferRequest.value = false
  pendingTransferResponseChatId.value = null
  clearTransferCountdown()

  if (transferResponse.is_transfered) {
    const transferredChat = buildTransferredChatModel(transferResponse, {
      fallbackContactName: transferRequest?.contactName,
      fallbackContactPhone: transferRequest?.contactPhone,
      fallbackCampaignId: transferRequest?.toCampaignId,
      fallbackCampaignName: transferRequest?.toCampaignName,
    })

    if (transferredChat) {
      whatsAppCoordinator.chatStore.addChat(transferredChat)
    }

    transferRequests.acceptTransfer(transferResponse.chat_id)
  } else {
    transferRequests.rejectTransfer(transferResponse.chat_id)

    if (transferRequest) {
      rejectedTransfer.value = {
        chatId: transferRequest.chatId,
        contactName: transferRequest.contactName,
        contactPhone: transferRequest.contactPhone,
      }
      showTransferRejectedDialog.value = true
    }
  }

  syncTransferRequestDialogState()
}

type ChatWithContactProfile = ApiChat & {
  contact?: { name?: string | null } | null
  contactProfile?: { name?: string | null } | null
  contact_profile?: { name?: string | null } | null
}

const getContactProfileName = (chat: ChatWithContactProfile): string | undefined => {
  return (
    chat.contactProfile?.name?.trim() ||
    chat.contact_profile?.name?.trim() ||
    chat.contact?.name?.trim() ||
    undefined
  )
}

const mapChatToUiModel = (chat: ApiChat, options?: { forceAssignedToCurrentUser?: boolean }) => {
  const statusUpper = chat.status.toUpperCase()
  let chatStatus = ChatStatus.PENDING

  if (statusUpper === 'ATENTION') {
    chatStatus = ChatStatus.ATTENTION
  } else if (statusUpper === 'IN_PROGRESS') {
    chatStatus = ChatStatus.IN_PROGRESS
  } else if (statusUpper === 'BOT') {
    chatStatus = ChatStatus.BOT
  } else if (statusUpper === 'FINISH') {
    chatStatus = ChatStatus.FINISH
  } else if (statusUpper === 'AUTO_CLOSED') {
    chatStatus = ChatStatus.AUTO_CLOSED
  } else if (statusUpper === 'ABANDON') {
    chatStatus = ChatStatus.ABANDON
  } else if (statusUpper === 'EXPIRE') {
    chatStatus = ChatStatus.EXPIRE
  } else if (statusUpper === 'TEMPLATE') {
    chatStatus = ChatStatus.TEMPLATE
  }

  const messages = chat.messages.map(normalizeApiMessage)

  messages.sort((a, b) => a.consecutive - b.consecutive)

  const lastVisibleMsg = getLastVisibleMessage(messages)
  const lastMessagePreview = getMessagePreviewText(lastVisibleMsg?.content)

  const chatUserId = options?.forceAssignedToCurrentUser ? authStore.userId || null : null

  // Contado en la consulta contra la marca de lectura del agente, no sobre los
  // 10 mensajes que trae cada chat: por eso la insignia sobrevive a la recarga.
  const unreadCount = chat.unread_count ?? 0

  return {
    id: chat.id,
    chat_id: chat.id,
    ...toChatIdentity(chat),
    contact_profile_name: getContactProfileName(chat),
    last_message: lastMessagePreview,
    last_message_time:
      lastVisibleMsg?.date ?? messages[messages.length - 1]?.date ?? new Date().toISOString(),
    unread_count: unreadCount,
    status: chatStatus,
    campaign_id: chat.campaign_id,
    campaign_name: campaignMap.value.get(chat.campaign_id)?.name,
    incoming_route_id: chat.incoming_route_id,
    messages: messages,
    user_id: chatUserId,
    created_at: chat.start_date ? String(chat.start_date) : undefined,
  }
}

// Tamano de cada tanda pedida al backend. Debe coincidir con el `size` por
// defecto de GET /oper/chats para que la pagina precargada (evento de registro
// de campana) cuente como la primera tanda real.
const CHATS_PAGE_SIZE = 30
const HISTORY_PAGE_SIZE = 10

const ensureCampaignNamesFor = async (chats: ApiChat[]) => {
  const hasMissingCampaignNames = chats.some(
    (chat) => chat.campaign_id && !campaignMap.value.has(chat.campaign_id),
  )
  if (!hasMissingCampaignNames) return

  try {
    const campaigns = await campaignService.getAllCampaigns()

    campaigns.forEach((campaign) => {
      campaignMap.value.set(campaign.id, campaign)
    })

    whatsAppCoordinator.setCampaignMap(campaignMap.value)
  } catch {}
}

// GET /oper/chats no sabe todo lo que la lista ya tiene: `messages` puede traer
// historial que el agente cargo hacia atras y que este endpoint no devuelve.
// Reemplazar el chat entero lo borraba, asi que se fusiona.
//
// Los mensajes y los campos de estado se tratan distinto a proposito. Los
// mensajes son acumulativos: unirlos por id nunca puede revertir nada, y son el
// hueco que el resync viene a tapar. Los campos de estado (status, asignacion)
// si se revierten si el snapshot es anterior al ultimo cambio local, y para eso
// esta `preferLocal`.
const mergeMessagesWithLocal = (
  existing: Message[],
  incoming: Message[],
  options?: { preferLocal?: boolean },
) => {
  if (incoming.length === 0) return existing

  const mergedById = new Map(existing.map((message) => [message.id, message]))
  let hasChanges = false

  incoming.forEach((message) => {
    // Con preferLocal el snapshot es anterior al ultimo cambio local, asi que
    // solo aporta los mensajes que faltan: reescribir uno que ya tenemos podria
    // devolverle un estado de entrega mas viejo que el que trajo el WS.
    if (options?.preferLocal && mergedById.has(message.id)) return

    mergedById.set(message.id, message)
    hasChanges = true
  })

  if (!hasChanges) return existing

  // Ordenar solo por consecutivo intercalaria las sesiones que trajo el historial
  // paginado: se reinicia en cada chat. `sortChatMessages` respeta el orden entre
  // sesiones que ya tenia el array y solo ordena dentro de cada una.
  return sortChatMessages([...mergedById.values()])
}

const mergeChatWithLocal = (
  incoming: WhatsAppChat,
  existing: WhatsAppChat,
  options?: { preferLocal?: boolean },
): WhatsAppChat => {
  const messages = mergeMessagesWithLocal(existing.messages, incoming.messages, options)
  const lastVisibleMsg = getLastVisibleMessage(messages)

  // preferLocal solo congela los campos de estado —el snapshot es anterior al
  // ultimo cambio local y los revertiria—, nunca los mensajes: esos son
  // acumulativos y son justo el hueco que el resync viene a tapar.
  const base = options?.preferLocal ? existing : incoming

  return {
    // `existing` va debajo para que sobrevivan los campos que el chat ya tenia y
    // el mapper no produce (`user_name`, `assigned_at`, `channel_id`,
    // `last_incoming_message_time`): con solo el spread de `base` el resync los
    // borraba en silencio. Hoy ninguno se pinta, pero el dia que se pinte el bug
    // aparece despues de cada reconexion y no se parece a su causa.
    ...existing,
    ...base,
    // `unread_count` viaja en el spread de `base`: manda el conteo del backend,
    // salvo con preferLocal —el snapshot es anterior a la ultima lectura del
    // agente y le devolveria la insignia que acaba de apagar.
    messages,
    last_message: getMessagePreviewText(lastVisibleMsg?.content),
    last_message_time:
      lastVisibleMsg?.date ?? messages[messages.length - 1]?.date ?? base.last_message_time,
  }
}

const applyChatsPage = async (
  chatsPage: ChatListResponse,
  seenChatIds: Set<string>,
  requestedAt: number,
) => {
  const responseChats = [...(chatsPage.assigned_chats || []), ...(chatsPage.pending_chats || [])]
  await ensureCampaignNamesFor(responseChats)

  const whatsappChats = [
    ...(chatsPage.assigned_chats || []).map((chat) =>
      mapChatToUiModel(chat, { forceAssignedToCurrentUser: true }),
    ),
    ...(chatsPage.pending_chats || []).map((chat) => mapChatToUiModel(chat)),
  ]

  const localChatsById = new Map(
    whatsAppCoordinator.chatStore.chats.map((chat) => [chat.chat_id, chat]),
  )

  whatsappChats.forEach((chat) => {
    // Se marca como visto aunque se descarte, para que la poda del final no lo
    // confunda con un chat que el backend dejo de devolver.
    seenChatIds.add(chat.chat_id)

    // El estado local se adelanto despues de que saliera esta peticion, asi que
    // el snapshot es anterior a ese cambio: aplicar sus campos revertiria lo que
    // el agente ya vio (un chat cerrado reapareciendo en atencion). Sus mensajes,
    // en cambio, si se aplican — ver mergeChatWithLocal.
    const isRevised = whatsAppCoordinator.chatStore.isChatRevisedSince(chat.chat_id, requestedAt)
    const existing = localChatsById.get(chat.chat_id)

    if (!existing) {
      // Sin copia local no hay nada que fusionar. Y si esta revisado sin estar en
      // la lista es porque el WS acaba de sacarlo: readmitirlo desde un snapshot
      // anterior a esa salida lo resucitaria.
      if (!isRevised) whatsAppCoordinator.chatStore.addChat(chat, { fromResync: true })
      return
    }

    whatsAppCoordinator.chatStore.addChat(
      mergeChatWithLocal(chat, existing, { preferLocal: isRevised }),
      { fromResync: true },
    )
  })
}

const CHATS_PAGE_MAX_ATTEMPTS = 3
const CHATS_PAGE_RETRY_DELAY_MS = 1000

const delay = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms))

// Solo tiene sentido reintentar lo que puede resolverse solo: un corte de red o
// un backend que todavia esta levantando. Un 4xx (sesion caida, peticion
// invalida) va a fallar igual las tres veces.
const isRetriableChatsError = (error: unknown) => {
  if (!isAxiosError(error)) return true

  const status = error.response?.status
  if (status === undefined) return true

  return status === 429 || status >= 500
}

// Una tanda que falla no se puede dar por vacia: sus chats se quedarian sin los
// mensajes que se publicaron con el socket caido, que es justo lo que el resync
// viene a recuperar. El fallo tipico es puntual —un 502 mientras el backend
// reinicia, que es cuando mas probable es que haya resync— asi que se reintenta
// con espera creciente antes de rendirse. Silencioso a proposito: el resync
// corre por debajo y el agente no tiene nada que hacer con el aviso.
const fetchChatsPage = async (
  userId: string,
  page: number,
  isCurrentRequest: () => boolean,
): Promise<{ response: ChatListResponse; requestedAt: number } | null> => {
  for (let attempt = 1; attempt <= CHATS_PAGE_MAX_ATTEMPTS; attempt++) {
    // La hora del intento que acabe funcionando: es contra ella que se compara
    // la marca de agua de cada chat.
    const requestedAt = Date.now()

    try {
      const response = await chatService.getChats(
        {
          user_id: userId,
          page,
          size: CHATS_PAGE_SIZE,
          // El backend pagina en este orden; el store vuelve a ordenar con el
          // mismo criterio para colocar los que entran por WebSocket.
          order: whatsAppCoordinator.chatStore.sortDirection,
        },
        { showErrorToast: false },
      )
      return { response, requestedAt }
    } catch (error) {
      if (attempt === CHATS_PAGE_MAX_ATTEMPTS || !isRetriableChatsError(error)) {
        console.warn(
          `[WhatsApp] La tanda ${page} de chats fallo tras ${attempt} intento(s); el resync queda incompleto:`,
          error,
        )
        return null
      }
    }

    await delay(CHATS_PAGE_RETRY_DELAY_MS * attempt)
    if (!isCurrentRequest()) return null
  }

  return null
}

const CHATS_RESYNC_MAX_RETRIES = 4
const CHATS_RESYNC_RETRY_BASE_DELAY_MS = 5000
const CHATS_RESYNC_RETRY_MAX_DELAY_MS = 30000

// loadChats() puede solaparse (login, reconexion WS, registro de campana). Cada
// llamada nueva invalida las tandas de fondo de la anterior, para que una carga
// vieja no sobreescriba con datos obsoletos.
let loadChatsRequestId = 0
let resyncRetryTimer: number | null = null

const cancelPendingResyncRetry = () => {
  if (resyncRetryTimer === null) return

  window.clearTimeout(resyncRetryTimer)
  resyncRetryTimer = null
}

// Un resync que se queda a medias deja chats sin los mensajes del hueco y sin
// nada que lo delate; el siguiente solo llegaria con la proxima reconexion, que
// puede tardar horas. Se reintenta entero —es idempotente gracias al merge— con
// espera creciente, y si aun asi no sale se le dice al agente en vez de dejarlo
// con una lista incompleta creyendo que esta al dia.
const scheduleResyncRetry = (requestId: number, attempt: number) => {
  cancelPendingResyncRetry()

  if (attempt > CHATS_RESYNC_MAX_RETRIES) {
    console.warn(
      `[WhatsApp] El resync de chats no se completo tras ${CHATS_RESYNC_MAX_RETRIES} reintentos`,
    )
    toast.add({
      severity: 'warn',
      summary: t('general.warning'),
      detail: t('whatsapp.error_chats_out_of_sync'),
      life: 8000,
    })
    return
  }

  const retryDelay = Math.min(
    CHATS_RESYNC_RETRY_BASE_DELAY_MS * 2 ** (attempt - 1),
    CHATS_RESYNC_RETRY_MAX_DELAY_MS,
  )

  resyncRetryTimer = window.setTimeout(() => {
    resyncRetryTimer = null

    // Un resync mas nuevo ya tomo el relevo: este reintento sobra.
    if (requestId !== loadChatsRequestId) return

    void loadChats(undefined, { resyncAttempt: attempt })
  }, retryDelay)
}

const loadChats = async (
  preloadedFirstPage?: ChatListResponse,
  options?: { resyncAttempt?: number },
) => {
  // Cualquier resync nuevo deja sin sentido el reintento que estuviera esperando.
  cancelPendingResyncRetry()

  if (!authStore.userId) return
  if (!preloadedFirstPage && !(await isRegisteredInCampaign())) return

  const resyncAttempt = options?.resyncAttempt ?? 0
  const requestId = ++loadChatsRequestId
  const userId = authStore.userId
  const isCurrentRequest = () => requestId === loadChatsRequestId

  // Instante de referencia del resync: nada que el WS o la UI adelanten despues
  // de este punto se pisa ni se poda. Para la tanda precargada (registro de
  // campana) tambien hace de hora de peticion — no se sabe cuando se pidio, pero
  // ese flujo llega con la lista vacia y no hay nada que revertir.
  const resyncStartedAt = Date.now()

  // Los chats que ya estaban al arrancar son los unicos candidatos a desaparecer
  // al final. Un NEW_CHAT que entre por WS durante el resync no sale en ninguna
  // tanda, y podarlo seria perder un chat recien llegado.
  const knownChatIds = new Set(
    whatsAppCoordinator.chatStore.chats.map((chat) => chat.chat_id),
  )
  const seenChatIds = new Set<string>()

  // La primera tanda pasa por el mismo camino que las demas: antes iba sin
  // proteger y un fallo ahi rechazaba la promesa, que ningun llamador espera
  // (todos hacen `void loadChats()`).
  let firstPage = preloadedFirstPage
  let firstPageRequestedAt = resyncStartedAt

  if (!firstPage) {
    const fetchedFirstPage = await fetchChatsPage(userId, 1, isCurrentRequest)
    if (!fetchedFirstPage) {
      if (isCurrentRequest()) scheduleResyncRetry(requestId, resyncAttempt + 1)
      return
    }

    firstPage = fetchedFirstPage.response
    firstPageRequestedAt = fetchedFirstPage.requestedAt
  }

  if (!isCurrentRequest()) return
  await applyChatsPage(firstPage, seenChatIds, firstPageRequestedAt)

  const totalPages = Math.max(
    Math.ceil((firstPage.assigned_total || 0) / CHATS_PAGE_SIZE),
    Math.ceil((firstPage.pending_total || 0) / CHATS_PAGE_SIZE),
  )

  for (let page = 2; page <= totalPages; page++) {
    if (!isCurrentRequest()) return

    const fetchedPage = await fetchChatsPage(userId, page, isCurrentRequest)

    // La tanda no se recupero ni con reintentos. Se corta aqui en vez de seguir
    // pidiendo: si el backend no responde, las tandas que faltan tampoco van a
    // salir y solo serviria para machacarlo. Sin la lista completa tampoco se
    // poda — no se distingue "el chat ya no existe" de "no alcance a verlo" —, y
    // el resync entero se reprograma para no dejar el hueco sin tapar.
    if (!fetchedPage) {
      if (isCurrentRequest()) scheduleResyncRetry(requestId, resyncAttempt + 1)
      return
    }
    if (!isCurrentRequest()) return

    await applyChatsPage(fetchedPage.response, seenChatIds, fetchedPage.requestedAt)
  }

  if (!isCurrentRequest()) return

  whatsAppCoordinator.chatStore.pruneStaleChats(knownChatIds, seenChatIds, resyncStartedAt)
}

const handleCampaignRegisteredEvent = (event: Event) => {
  const customEvent = event as CustomEvent<{ chatsResponse?: ChatListResponse }>
  void loadChats(customEvent.detail?.chatsResponse)
}

// El resync vive en el watch de `isConnected`: el unico hueco real es el de una
// reconexion, porque los eventos publicados mientras el socket estaba caido no se
// reproducen. Estar en otra pestana no abre ningun hueco —los frames llegan y se
// procesan igual, y si el navegador congela la pestana los encola hasta que
// despierta—, asi que recargar por tiempo escondido solo costaba una tanda de
// peticiones por cada regreso.
const hasLostWebSocketConnection = ref(false)

onMounted(async () => {
  try {
    notifications.initializeAudio()

    if (!authStore.userId) {
      return
    }

    whatsAppCoordinator.webSocketStore.on('USER_TOOK_CHAT', handleUserTookChatConfirmation)

    try {
      const campaigns = await campaignService.getAllCampaigns({
        user_id: authStore.userId,
      })

      campaigns.forEach((campaign) => {
        campaignMap.value.set(campaign.id, campaign)
      })

      whatsAppCoordinator.setCampaignMap(campaignMap.value)
    } catch {}

    await whatsAppCoordinator.initialize(authStore.userId, UserRole.OPERATOR)

    void loadChats()

    whatsAppCoordinator.webSocketStore.on('TRANSFER_ACCEPTED', handleTransferAcceptedResponse)

    window.addEventListener('notification-click', handleNotificationClick)

    window.addEventListener('transfer-request', handleTransferRequestEvent as EventListener)

    window.addEventListener('transfer-rejected', handleTransferRejectedEvent as EventListener)

    window.addEventListener('whatsapp-campaign-registered', handleCampaignRegisteredEvent)

    window.addEventListener('keydown', handleEscapeKeydown)

    openStartConversationFromRoute()
    void applyChatListTabFromRoute()
  } catch {}
})

onUnmounted(() => {
  whatsAppCoordinator.webSocketStore.off('USER_TOOK_CHAT', handleUserTookChatConfirmation)
  whatsAppCoordinator.webSocketStore.off('TRANSFER_ACCEPTED', handleTransferAcceptedResponse)
  window.removeEventListener('notification-click', handleNotificationClick)
  window.removeEventListener('transfer-request', handleTransferRequestEvent as EventListener)
  window.removeEventListener('transfer-rejected', handleTransferRejectedEvent as EventListener)
  window.removeEventListener('whatsapp-campaign-registered', handleCampaignRegisteredEvent)
  window.removeEventListener('keydown', handleEscapeKeydown)
  cancelPendingResyncRetry()

  isAwaitingTransferToUserResponse.value = false
  pendingTransferToUserChatId.value = null
  pendingTransferToUserInfo.value = null
  clearTransferCountdown()
  pendingTakeChatIds.value = new Set()
})

const handleNotificationClick = (event: Event) => {
  const customEvent = event as CustomEvent
  const { chatId } = customEvent.detail

  if (chatId) {
    handleSelectChat(chatId)
  }
}

const handleTransferRequestEvent = (event: Event) => {
  const customEvent = event as CustomEvent
  const transferData = customEvent.detail

  transferRequests.addTransferRequest(transferData)

  showTransferRequestDialog.value = true

  if (!isProcessingTransferRequest.value && transferCountdownInterval.value === null) {
    startTransferCountdown()
  }
}

const handleTransferRejectedEvent = (event: Event) => {
  const customEvent = event as CustomEvent
  const { chatId, contactName, contactPhone } = customEvent.detail
  const pendingRejectedTransferInfo =
    pendingTransferToUserChatId.value === chatId ? pendingTransferToUserInfo.value : null

  if (pendingTransferToUserChatId.value === chatId) {
    isAwaitingTransferToUserResponse.value = false
    pendingTransferToUserChatId.value = null
    showTransferDialog.value = false
  }

  rejectedTransfer.value = pendingRejectedTransferInfo ?? {
    chatId,
    contactName,
    contactPhone,
  }
  pendingTransferToUserInfo.value = null

  showTransferRejectedDialog.value = true
}

const handleSelectChat = async (chatId: string) => {
  await whatsAppCoordinator.selectChat(chatId)
  showMobileChatList.value = false
}

const handleLoadSelectedChatHistory = async () => {
  if (!selectedChat.value) return

  const chatId = selectedChat.value.chat_id
  if (loadingHistoryChatIds.has(chatId) || exhaustedHistoryChatIds.has(chatId)) return

  // La primera pagina arranca en el mensaje mas viejo que ya se ve: el chat llega de la
  // lista con los ultimos mensajes de la sesion, y pedir sin cursor los repetiria.
  const beforeMessageId = historyCursorByChatId.get(chatId) ?? selectedChat.value.messages[0]?.id

  loadingHistoryChatIds.add(chatId)

  try {
    const history = await chatService.getChatHistory(chatId, {
      limit: HISTORY_PAGE_SIZE,
      ...(beforeMessageId ? { before_message_id: beforeMessageId } : {}),
    })

    if (history.length) {
      // El cursor sale de la respuesta cruda, que viene en el orden del historial
      // (sesion, consecutivo): el mismo que entiende el backend.
      historyCursorByChatId.set(chatId, history[0].id)
      whatsAppCoordinator.chatStore.prependHistoricalMessages(
        chatId,
        history.map(normalizeApiMessage),
      )
    }

    if (history.length < HISTORY_PAGE_SIZE) exhaustedHistoryChatIds.add(chatId)
  } catch {
  } finally {
    loadingHistoryChatIds.delete(chatId)
  }
}

const handleTakeChat = async () => {
  if (!selectedChat.value) return
  const chatId = String(selectedChat.value.chat_id)

  if (pendingTakeChatIds.value.has(chatId)) return

  addPendingTakeChat(chatId)

  try {
    await whatsAppCoordinator.takeChat(chatId)
  } catch {
    clearPendingTakeChat(chatId)
  }
}

const handleCloseChat = () => {
  whatsAppCoordinator.exitCurrentChat()

  showContactInfoPanel.value = false
  showMobileChatList.value = true
}

const isDialogOpen = () => {
  return Boolean(document.querySelector('.p-dialog-mask, [role="dialog"]'))
}

const handleEscapeKeydown = (event: KeyboardEvent) => {
  if (event.key !== 'Escape') return
  if (event.repeat) return
  if (!selectedChat.value) return
  if (isDialogOpen()) return

  handleCloseChat()
}

// El chat viene en el evento, no se lee del seleccionado: un envio con
// traduccion vuelve del backend unos segundos despues y el agente puede estar
// ya en otra conversacion, que es donde acababa el mensaje.
const handleSendMessage = async (data: {
  chatId: string
  text: string
  draftText: string
  replyToId?: string
  aiTranslate?: Message['content']['ai_translate']
}) => {
  if (!data.chatId) return

  try {
    await whatsAppCoordinator.sendTextMessage(
      data.chatId,
      data.text,
      data.replyToId,
      data.aiTranslate,
    )
  } catch (error) {
    // El envio no dejo nada en el hilo: el texto solo existia en el editor, que
    // el `ChatView` ya vacio. Se devuelve al borrador para no perderlo.
    if (error instanceof MessageNotQueuedError) {
      // Salvo que el agente ya haya escrito otra cosa en ese chat mientras
      // tanto: lo suyo es mas reciente y no se pisa.
      if (!chatDraftStore.getDraft(data.chatId)) {
        chatDraftStore.setDraft(data.chatId, data.draftText)
      }

      toast.add({
        severity: 'warn',
        summary: t('general.warning'),
        detail: t('whatsapp.error_message_not_sent_draft_kept'),
        life: 6000,
      })
      return
    }

    // Aqui si quedo el mensaje en el hilo marcado como fallido: devolver el
    // texto al editor lo duplicaria.
    toast.add({
      severity: 'warn',
      summary: t('general.warning'),
      detail: t('whatsapp.error_message_send_failed'),
      life: 6000,
    })
  }
}

watch(isConnected, (connected, wasConnected) => {
  if (!connected) {
    hasLostWebSocketConnection.value = true
    return
  }

  if (!wasConnected && hasLostWebSocketConnection.value) {
    hasLostWebSocketConnection.value = false
    void loadChats()
  }
})

watch(selectedChatId, (newChatId) => {
  selectedContactInfo.value = null
  contactInfoError.value = null

  if (newChatId) {
    // No se consulta el contacto al abrir un chat: el nombre que pinta la
    // lista ya viene del backend en chat.name, que WhatsappService sincroniza
    // con el contacto de la campana en cada mensaje entrante. La API solo hace
    // falta para el panel de ficha (estado, email, campos personalizados), y
    // por eso solo se pide cuando ese panel esta abierto.
    if (showContactInfoPanel.value) {
      void loadSelectedContactInfo()
    }
  }
})

watch(
  () => [route.query.open, route.query.campaignId],
  () => {
    openStartConversationFromRoute()
  },
)

watch(
  () => route.query.tab,
  () => {
    void applyChatListTabFromRoute()
  },
)

watch(showTemplateDialog, (visible) => {
  if (visible || route.query.open !== 'start-conversation') return

  const { open, campaignId, ...query } = route.query
  void router.replace({ query })
})

// El chat viene en el evento por lo mismo que en `handleSendMessage`: el
// fichero se lee en un `FileReader` y el emit llega despues, con el agente
// quiza ya en otra conversacion.
const handleSendFile = async (data: {
  chatId: string
  base64: string
  fileName: string
  contentType: string
  extension: string
  caption?: string
  isVoiceNote?: boolean
}) => {
  if (!data.chatId) return

  const byteCharacters = atob(data.base64.split(',')[1] || data.base64)
  const byteArray = new Uint8Array(Array.from(byteCharacters).map((char) => char.charCodeAt(0)))
  const file = new File([byteArray], data.fileName, { type: data.contentType })

  try {
    await whatsAppCoordinator.sendFileMessage(data.chatId, file, data.caption, data.isVoiceNote)
  } catch {
    // El archivo no se puede devolver a ningun sitio —la vista previa ya se
    // cerro y el `File` se descarto—, pero fallar en silencio es peor.
    toast.add({
      severity: 'warn',
      summary: t('general.warning'),
      detail: t('whatsapp.error_file_send_failed'),
      life: 6000,
    })
  }
}

const handleEndChat = () => {
  whatsAppCoordinator.endCurrentChat()

  showContactInfoPanel.value = false
  showMobileChatList.value = true
}

const handleSendTemplate = async (data: {
  contacts: ContactSelection[]
  template: Template
  parameters: TemplateSendComponent[] | null
  destination: RoutingDestination
  campaign_id?: string
  incoming_route_id?: string
  chat_id?: string
  is_bulk?: boolean
  confirm_takeover?: boolean
}) => {
  try {
    const routeId = data.incoming_route_id || null
    const account_id = data.template.account_id
    const isSingleContact = data.contacts.length === 1

    if (data.chat_id && isSingleContact) {
      await whatsAppCoordinator.sendTemplate(
        data.contacts,
        data.template,
        data.destination,
        routeId,
        account_id,
        data.parameters,
        data.campaign_id,
        data.chat_id,
        data.is_bulk ?? false,
        data.confirm_takeover ?? false,
      )
    } else {
      await whatsAppCoordinator.sendTemplate(
        data.contacts,
        data.template,
        data.destination,
        routeId,
        account_id,
        data.parameters,
        data.campaign_id,
        undefined,
        data.is_bulk ?? false,
        data.confirm_takeover ?? false,
      )
    }

    showTemplateDialog.value = false
  } catch (error) {}
}

const handleOpenTransferDialog = () => {
  transferDialogRestoreDraft.value = null
  showTransferDialog.value = true
}

type TransferChatData =
  | {
      chatId: string
      transferType: 'campaign'
      toCampaignId: string
      toCampaignName: string
    }
  | {
      chatId: string
      transferType: 'user'
      toCampaignId: string
      toCampaignName: string
      toUserId: string
      toUserName: string
      transfer_message?: string
    }
  | {
      chatId: string
      transferType: 'bot'
      toBotId: string
      toBotName: string
    }

const handleTransferChat = async (data: TransferChatData) => {
  if (!selectedChat.value) {
    return
  }

  try {
    if (data.transferType === 'user') {
      isAwaitingTransferToUserResponse.value = true
      pendingTransferToUserChatId.value = data.chatId
      pendingTransferToUserInfo.value = {
        chatId: data.chatId,
        contactName: selectedChat.value.contact_name,
        contactPhone: getContactIdentifier(
          selectedChat.value.contact_phone,
          selectedChat.value.contact_username,
        ),
        destinationName: data.toUserName,
        destinationSubtitle: data.toCampaignName,
        destinationMeta: null,
        retryTransferData: data,
      }

      await whatsAppCoordinator.transferChatToUser(
        data.chatId,
        selectedChat.value.campaign_id,
        selectedChat.value.campaign_name || '',
        data.toUserId,
        data.toUserName,
        data.toCampaignId,
        data.toCampaignName,
        selectedChat.value.contact_name,
        selectedChat.value.contact_phone,
        data.transfer_message || '',
      )
      return
    }

    if (data.transferType === 'campaign') {
      await whatsAppCoordinator.transferChatToCampaign(
        data.chatId,
        data.toCampaignId,
        data.toCampaignName,
      )
    } else if (data.transferType === 'bot') {
      await whatsAppCoordinator.transferChatToBot(
        data.chatId,
        data.toBotId,
        data.toBotName,
        selectedChat.value.campaign_id,
        selectedChat.value.incoming_route_id,
        selectedChat.value.contact_phone,
      )
    }

    showTransferDialog.value = false
  } catch (error) {
    if (data.transferType === 'user') {
      isAwaitingTransferToUserResponse.value = false
      pendingTransferToUserChatId.value = null
      pendingTransferToUserInfo.value = null
    }
  }
}

const handleAcceptTransfer = (chatId: string) => {
  void submitTransferDecision(chatId, true)
}

const handleRejectTransfer = (chatId: string) => {
  void submitTransferDecision(chatId, false)
}

const queueTransferDialogRestore = (
  action: 'type' | 'retry',
  transferData?: Extract<TransferChatData, { transferType: 'user' }> | null,
) => {
  transferDialogRestoreDraft.value = {
    nonce: Date.now(),
    action,
    transferData: transferData ?? null,
  }
}

const handleBackToChatFromRejected = () => {
  showTransferRejectedDialog.value = false
}

const handleChangeDestinationFromRejected = () => {
  showTransferRejectedDialog.value = false
  queueTransferDialogRestore('type')
  showTransferDialog.value = true
}

const handleRetryRejectedTransfer = () => {
  const retryTransferData = rejectedTransfer.value?.retryTransferData
  if (!retryTransferData) return

  showTransferRejectedDialog.value = false
  queueTransferDialogRestore('retry', retryTransferData)
  showTransferDialog.value = true
}

watch(currentTransferRequest, (request) => {
  if (!showTransferRequestDialog.value) return
  if (isProcessingTransferRequest.value) return

  if (request) {
    startTransferCountdown()
  } else {
    clearTransferCountdown()
  }
})
</script>

<style scoped>
.contact-info-panel-enter-active,
.contact-info-panel-leave-active {
  transition:
    transform 0.25s ease,
    opacity 0.2s ease;
}

.contact-info-panel-enter-from,
.contact-info-panel-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

@media (prefers-reduced-motion: reduce) {
  .contact-info-panel-enter-active,
  .contact-info-panel-leave-active {
    transition: none;
  }
}
</style>
